// @workflow assistant.ui-tools
/**
 * Open a task on its task board: the board's Kanban with Edit task open on the task. The one action
 * behind the assistant's timeline "Open in task board" (Assistant/components/parts/TaskPopover.vue)
 * and the UI tool `ui.tasks.open_on_board` (Assistant/domain/taskBoardTools.js). No share-intent URL:
 * the share link `?intent=task-board` plays only when the facility page mounts, and turns a task with
 * 3D tags into the fullscreen 3D viewer (utils/shareIntents/taskBoardIntent.js), not the board.
 *
 * Kanban opens Edit task from `?taskId=…&editTask=1` (Task/utils/taskEditQuery.js) whenever its route
 * changes, and highlights the card; without Kanban permission its guard sends the user to Gantt, which
 * opens Edit task from the same query. Edit task then registers the task on screen (actions/openTask.js),
 * which is how a caller reads back that it opened.
 * Relative imports only, so `npm run test:unit` (node --test) can load it directly.
 */
import { synthesizeKanbanRoute } from '../utils/shareIntents/utils/synthesizeKanbanRoute.js';
import { taskEditQuery, TASK_QUERY } from '../features/Task/utils/taskEditQuery.js';
import { requestShowTask } from './shownBoardTask.js';

export const TASK_BOARD_OPEN = Object.freeze({
  /** The board is opening in this tab; Edit task opens on it once the board is there. */
  OPENING: 'opening',
  /** Another facility's board opened in a new tab (the app's own path across facilities). */
  NEW_TAB: 'new-tab',
  /** The task's id, board or facility is missing: nothing opened. */
  NO_TASK: 'no-task',
});

/** How a board in another facility opens: a new tab, cut off from this one. */
const NEW_TAB = Object.freeze({ TARGET: '_blank', FEATURES: 'noopener,noreferrer' });

/** App.vue picks the layout from `route.meta.layout`; a facility's pages (viewer, task board) share this one. */
const FACILITY_LAYOUT = 'viewer-layout';

/**
 * The facility whose pages are open now: the route id on the facility layout, else null. Moving
 * between two facilities on that layout does not reload the facility (App.vue reuses the layout,
 * and layouts/view.vue runs INITIALIZE_MAIN_DATA only when it is created).
 * @param {{ params?: object, meta?: object }} [route]
 * @returns {string|null}
 */
export function facilityInView(route) {
  const id = route && route.params && route.params.id;
  if (!id || !route.meta || route.meta.layout !== FACILITY_LAYOUT) return null;
  return String(id);
}

/**
 * A task's board id from the app's task read (`TASK/GET_BY_ID`, `GET api/Tasks/{id}`): the axios
 * response whose body is the API's TaskVM, where the board is `typeId` (aiva.api TaskVM.TypeId, camelCase
 * like every MVC response; the assistant's MCP reads the same field, TaskTools.Writes.cs).
 * @param {object|null} read the action's answer (a response, or the body itself)
 * @returns {string|null} null when the read has no task or the task no board
 */
export function taskBoardIdOf(read) {
  const task = read && read.data !== undefined ? read.data : read;
  const id = task && task.typeId;
  return typeof id === 'string' && id ? id : null;
}

/**
 * @param {{ taskId: string, taskTypeId: string, facilityId: string }} task the task, its board and its facility
 * @returns {object} the route: the board's Kanban with an edit request for the task
 */
export function taskBoardLocation({ taskId, taskTypeId, facilityId }) {
  return { ...synthesizeKanbanRoute({ taskTypeId }, facilityId), query: taskEditQuery(taskId) };
}

/**
 * Where the task opens. Another facility than the one in view opens in a new tab, the path the app
 * itself takes across facilities (Task/utils/taskIsolation.js navigateToFacility, store/modules/tags.js).
 * @param {{ taskId?: string, taskTypeId?: string, facilityId?: string }|null} task
 * @param {string|null} currentFacilityId facilityInView(route)
 * @returns {{ location: object, newTab: boolean }|null} null without all three ids
 */
export function taskBoardTarget(task, currentFacilityId) {
  if (!task || !task.taskId || !task.taskTypeId || !task.facilityId) return null;
  return {
    location: taskBoardLocation(task),
    newTab: !!currentFacilityId && currentFacilityId !== task.facilityId,
  };
}

/**
 * Open the task on its board.
 * @param {{
 *   router: { push: (location: object) => Promise<*> },
 *   isNavigationFailure: (err: *) => boolean,   VueRouter.isNavigationFailure: already there, or a guard sent the user on
 *   openTab?: (location: object) => void,         opens a route in a new tab (default: the router's href, window.open)
 *   stepAside?: () => void,                       makes room for the board (the assistant leaves page mode)
 * }} ctx
 * @param {{ taskId?: string, taskTypeId?: string, facilityId?: string }} task
 * @param {string|null} currentFacilityId facilityInView(route)
 * @returns {Promise<{ status: string, location?: object }>} a TASK_BOARD_OPEN status; any other navigation error throws
 */
export async function openTaskOnBoard({ router, isNavigationFailure, openTab, stepAside }, task, currentFacilityId) {
  const target = taskBoardTarget(task, currentFacilityId);
  if (!target) return { status: TASK_BOARD_OPEN.NO_TASK };
  if (target.newTab) {
    if (openTab) openTab(target.location);
    else globalThis.open(router.resolve(target.location).href, NEW_TAB.TARGET, NEW_TAB.FEATURES);
    return { status: TASK_BOARD_OPEN.NEW_TAB, location: target.location };
  }
  if (stepAside) stepAside();
  try {
    await router.push(target.location);
  } catch (err) {
    if (!isNavigationFailure(err)) throw err;
  }
  return { status: TASK_BOARD_OPEN.OPENING, location: target.location };
}

/**
 * Show the task on its board (`ui.tasks.show_on_board`): the board route only, then an in-app request
 * (actions/shownBoardTask.js), so Kanban highlights the card and scrolls it into view and Edit task stays closed.
 * Asking twice for the card already on screen shows it again (a URL flag would not change). Another facility's
 * board opens in a new tab with the notification link (`?taskId`), which highlights the card there.
 * @param {{ router: object, isNavigationFailure: (err: *) => boolean, openTab?: (location: object) => void,
 *   stepAside?: () => void }} ctx as openTaskOnBoard
 * @param {{ taskId?: string, taskTypeId?: string, facilityId?: string }} task
 * @param {string|null} currentFacilityId facilityInView(route)
 * @returns {Promise<{ status: string, location?: object }>} a TASK_BOARD_OPEN status
 */
export async function showTaskOnBoard({ router, isNavigationFailure, openTab, stepAside }, task, currentFacilityId) {
  const target = taskBoardTarget(task, currentFacilityId);
  if (!target) return { status: TASK_BOARD_OPEN.NO_TASK };
  const board = synthesizeKanbanRoute({ taskTypeId: task.taskTypeId }, task.facilityId);
  if (target.newTab) {
    const link = { ...board, query: { [TASK_QUERY.TASK_ID]: String(task.taskId) } };
    if (openTab) openTab(link);
    else globalThis.open(router.resolve(link).href, NEW_TAB.TARGET, NEW_TAB.FEATURES);
    return { status: TASK_BOARD_OPEN.NEW_TAB, location: link };
  }
  if (stepAside) stepAside();
  try {
    await router.push(board);
  } catch (err) {
    if (!isNavigationFailure(err)) throw err;
  }
  // After the push: a board already on screen hears it at once, a board still loading takes it when its tasks arrive.
  requestShowTask({ taskId: task.taskId, boardId: task.taskTypeId });
  return { status: TASK_BOARD_OPEN.OPENING, location: board };
}
